/** Fired whenever the embedded pentacode app navigates internally. */
export const ROUTE_CHANGE_EVENT = "pentacode-route-change";
/** Fired once the embedded app finished mounting and is interactive. */
export const READY_EVENT = "pentacode-ready";
/** Fired on load, mount or auth failures. */
export const ERROR_EVENT = "pentacode-error";

export interface PentacodeRouteChangeDetail {
    /** pentacode-internal route, e.g. "roster" or "roster?venue=1" */
    route: string;
    /** True for silent corrections (default params etc.) — hosts should map
     * this to a history *replace* so their URL history doesn't fill up. */
    replace?: boolean;
}

export interface PentacodeErrorDetail {
    message: string;
    cause?: unknown;
    /**
     * `icon-sprite`: the host's `icon-sprite` didn't load; the app keeps running with its current icons.
     * `route-denied`: the requested route isn't available embedded or to this user; the app redirected to the first
     * accessible page, which follows as a route change.
     * `not-authenticated` (`<pentacode-view>`): nobody is logged in yet; the view renders once a session exists.
     * `route-unavailable` (`<pentacode-view>`): the route exists but can't render as a standalone view yet.
     */
    code?: "icon-sprite" | "route-denied" | "not-authenticated" | "route-unavailable";
}

/**
 * The `<pentacode-app>` element. Give it an explicit height; the app fills it.
 *
 * Attributes: `route`, `icon-sprite` and `color-scheme`, mirrored by the properties below.
 *
 * Slot `submenu`: a `slot="submenu"` child replaces the submenu of the section currently shown (settings, employees,
 * exports). The submenu column then takes the child's size, so an empty child removes it; the employee header above the
 * employees submenu stays. Without a child, pentacode shows its own submenu.
 */
export interface PentacodeAppElement extends HTMLElement {
    /** pentacode-internal route to display, e.g. "roster" or "roster?venue=1"; mirrors the `route` attribute. */
    route: string;
    /** URL of an svg sprite with a `<symbol>` per icon token; icons it lacks come from the default sprite. */
    iconSprite: string;
    /** `light`, `dark` or `system` (the default): `system` follows `prefers-color-scheme`. */
    colorScheme: string;
}

// <generated:routes>
/** A route `<pentacode-app>` can show; set it as `route`, optionally with a `?query`. */
export type PentacodeRoute =
    | "employees"
    | `employees/${number}`
    | `employees/${number}/absences`
    | `employees/${number}/advances`
    | `employees/${number}/availabilities`
    | `employees/${number}/bonuses-balance`
    | `employees/${number}/contracts`
    | `employees/${number}/core-data`
    | `employees/${number}/documents`
    | `employees/${number}/ledgers`
    | `employees/${number}/payroll-data`
    | `employees/${number}/permissions`
    | `employees/${number}/positions`
    | `employees/${number}/time`
    | `employees/${number}/time-balance`
    | `employees/${number}/vacation-balance`
    | "employees/all"
    | "employees/all/absences"
    | "employees/all/advances"
    | "employees/all/availabilities"
    | "employees/all/bonuses-balance"
    | "employees/all/contracts"
    | "employees/all/core-data"
    | "employees/all/documents"
    | "employees/all/ledgers-month"
    | "employees/all/ledgers-year"
    | "employees/all/permissions"
    | "employees/all/positions"
    | "employees/all/time"
    | "employees/all/time-balance"
    | "employees/all/vacation-balance"
    | "exports"
    | "exports/balances-average-work-time-excel"
    | "exports/balances-bonuses-excel"
    | "exports/balances-overtime-excel"
    | "exports/balances-time-excel"
    | "exports/balances-vacation-excel"
    | "exports/cashbook-datev"
    | "exports/cashbook-datev-online"
    | "exports/cashbook-generic"
    | "exports/cashbook-lexware"
    | "exports/cashbook-pdf"
    | "exports/employee-data-csv"
    | "exports/employee-data-pdf"
    | "exports/employee-table-excel"
    | "exports/payroll-addison"
    | "exports/payroll-agenda"
    | "exports/payroll-agenda-calendar"
    | "exports/payroll-agenda-employee-core-data"
    | "exports/payroll-bmd"
    | "exports/payroll-bmd-absences"
    | "exports/payroll-datev-lg"
    | "exports/payroll-datev-lodas"
    | "exports/payroll-lexware"
    | "exports/payroll-lohn-ag"
    | "exports/payroll-report-detailed"
    | "exports/payroll-report-overview"
    | "exports/payroll-sage"
    | "exports/roster-excel"
    | "exports/time-sheet"
    | "issues"
    | "recruiting"
    | "roster"
    | "settings"
    | "settings/account"
    | "settings/accounting"
    | "settings/ai-assistant"
    | "settings/benefits"
    | "settings/bonuses"
    | "settings/company"
    | "settings/core-data"
    | "settings/documents"
    | "settings/time"
    | "settings/venues"
    | "time";
// </generated:routes>

declare global {
    interface HTMLElementTagNameMap {
        "pentacode-app": PentacodeAppElement;
    }

    /** pentacode's events. All bubble and are composed, so hosts can listen on any ancestor. */
    interface HTMLElementEventMap {
        [ROUTE_CHANGE_EVENT]: CustomEvent<PentacodeRouteChangeDetail>;
        [READY_EVENT]: CustomEvent<void>;
        [ERROR_EVENT]: CustomEvent<PentacodeErrorDetail>;
    }
}
